import { useState, useEffect } from 'react';
import { useLegacyHoursRecovery } from '@/components/planning/useLegacyHoursRecovery';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Truck, LayoutDashboard, Camera, History, AlertTriangle,
  Calendar, Bell, Users, Settings, LogOut, Menu, X, ChevronRight, ChevronDown, CheckCircle, BarChart3, Package, MessageSquare, BookOpen, Coffee, Briefcase, Calculator, Clock, Mail, Video, Home, FileText, Euro, UserCheck, ArrowLeft, CreditCard, Smartphone, Shield, Fuel, Gauge, ClipboardList
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { INTERNAL_ROLE, canManageInternal } from '@/components/internal-staff/shared';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_imageblaze/artifacts/5e5w021j_7D777DBC-744F-44B3-9C70-7AE1E6146D31.png";

// Define module configurations
const MODULE_CONFIG = {
  b2b: {
    name: 'Livraison B2B',
    color: 'slate',
    basePath: '/admin/b2b'
  },
  b2c: {
    name: 'Livraison B2C',
    color: 'blue',
    basePath: '/admin/b2c'
  },
  demenagement: {
    name: 'Déménagement',
    color: 'orange',
    basePath: '/admin/demenagement'
  }
};

const Layout = ({ user, children, activeModule = null }) => {
  useLegacyHoursRecovery(user);
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [pendingValidationsCount, setPendingValidationsCount] = useState(0);
  const [pendingDemandesCount, setPendingDemandesCount] = useState(0);
  const [pendingFormationsCount, setPendingFormationsCount] = useState(0);
  const [pendingNotificationsCount, setPendingNotificationsCount] = useState(0);
  const [pendingCandidaturesCount, setPendingCandidaturesCount] = useState(0);
  const [trialAlertsCount, setTrialAlertsCount] = useState(0);
  const [b2cNewCount, setB2cNewCount] = useState(0);
  const [demenagementNewCount, setDemenagementNewCount] = useState(0);

  const normalizeCount = (value) => {
    const num = Number(value);
    return Number.isFinite(num) && num >= 0 ? num : 0;
  };

  const isAdmin = user?.role === 'admin';
  const isFullAdmin = user?.admin_level === 'full' || !user?.admin_level;
  const isLimitedAdmin = user?.admin_level === 'limited';
  const isColisPriveAdmin = user?.admin_level === 'colis_prive';
  const userPermissions = Array.isArray(user?.permissions) ? user.permissions : [];
  
  // Super admin = seul transport@dlv-express.com peut gérer les autres admins
  const isSuperAdmin = user?.is_super_admin === true || user?.email === 'transport@dlv-express.com';

  // Check if employee profile is complete - only check if user is loaded and is an employee
  const isEmployee = user?.role === 'employee';
  
  // Helper to check if a field has a valid value (not null, undefined, or empty string after trim)
  const hasValue = (val) => val && typeof val === 'string' && val.trim().length > 0;
  
  // Check all required profile fields have valid values
  const isEmployeeProfileComplete = hasValue(user?.first_name) && 
                                    hasValue(user?.last_name) && 
                                    hasValue(user?.phone) && 
                                    hasValue(user?.address) && 
                                    hasValue(user?.postal_code) && 
                                    hasValue(user?.city) && 
                                    hasValue(user?.email);
  
  const isOnProfilePage = location.pathname === '/profile';
  
  // Debug logging for profile completeness (remove in production after fix verified)
  // console.log('Profile check:', { isEmployee, isEmployeeProfileComplete, fields: { first_name: user?.first_name, last_name: user?.last_name, phone: user?.phone, address: user?.address, postal_code: user?.postal_code, city: user?.city } });
  
  // If employee profile is incomplete, block access to everything except profile page
  const shouldBlockEmployeeAccess = isEmployee && user?.user_id && !isEmployeeProfileComplete && !isOnProfilePage;

  // Redirect employee to profile page if trying to access other pages with incomplete profile
  useEffect(() => {
    if (shouldBlockEmployeeAccess) {
      navigate('/profile');
    }
  }, [shouldBlockEmployeeAccess, navigate]);

  // Toggle section expand/collapse
  const toggleSection = (sectionTitle) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle]
    }));
  };

  // Auto-expand section containing current path
  useEffect(() => {
    if (currentModule === 'b2b' && isAdmin && !isLimitedAdmin && !isColisPriveAdmin) {
      const currentPath = location.pathname;
      b2bAdminSections.forEach(section => {
        const hasActiveLink = section.links.some(link => currentPath === link.path);
        if (hasActiveLink) {
          setExpandedSections(prev => ({
            ...prev,
            [section.title]: true
          }));
        }
      });
    }
  }, [location.pathname]);

  // Determine current module from path or prop
  const getCurrentModule = () => {
    if (activeModule) return activeModule;
    const path = location.pathname;
    if (path.startsWith('/admin/b2c')) return 'b2c';
    if (path.startsWith('/admin/demenagement')) return 'demenagement';
    if (path.startsWith('/admin/b2b') || path.startsWith('/admin')) return 'b2b';
    return null;
  };

  const currentModule = getCurrentModule();
  const moduleConfig = currentModule ? MODULE_CONFIG[currentModule] : null;

  // Fetch all pending counts for admins
  useEffect(() => {
    const fetchPendingCounts = async () => {
      if (!isAdmin) return;
      
      try {
        const validationsRes = await axios.get(`${API}/photos/pending-validations`, { withCredentials: true });
        setPendingValidationsCount(normalizeCount(validationsRes.data?.count));
      } catch (error) {
        console.error('Failed to fetch pending validations:', error);
        setPendingValidationsCount(0);
      }

      try {
        const demandesRes = await axios.get(`${API}/demandes?status=pending`, { withCredentials: true });
        const pendingDemandes = Array.isArray(demandesRes.data)
          ? demandesRes.data.filter((d) => ['pending', 'partial'].includes(d.status)).length
          : 0;
        setPendingDemandesCount(normalizeCount(pendingDemandes));
      } catch (error) {
        console.error('Failed to fetch pending demandes:', error);
        setPendingDemandesCount(0);
      }

      try {
        const formationsRes = await axios.get(`${API}/formations/pending-count`, { withCredentials: true });
        setPendingFormationsCount(normalizeCount(formationsRes.data?.count));
      } catch (error) {
        setPendingFormationsCount(0);
      }

      try {
        const notifRes = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
        setPendingNotificationsCount(normalizeCount(notifRes.data?.count));
      } catch (error) {
        setPendingNotificationsCount(0);
      }

      try {
        const candRes = await axios.get(`${API}/candidatures/count`, { withCredentials: true });
        setPendingCandidaturesCount(normalizeCount(candRes.data?.count));
      } catch (error) {
        setPendingCandidaturesCount(0);
      }

      try {
        const trialRes = await axios.get(`${API}/trial-periods/alerts-count`, { withCredentials: true });
        setTrialAlertsCount(normalizeCount(trialRes.data?.count));
      } catch (error) {
        setTrialAlertsCount(0);
      }

      // B2C and Demenagement counts
      try {
        const b2cRes = await axios.get(`${API}/demandes-transport-b2c?status=nouveau`, { withCredentials: true });
        setB2cNewCount(Array.isArray(b2cRes.data) ? b2cRes.data.length : 0);
      } catch (error) {
        setB2cNewCount(0);
      }

      try {
        const demRes = await axios.get(`${API}/demandes-client?status=nouveau`, { withCredentials: true });
        setDemenagementNewCount(Array.isArray(demRes.data) ? demRes.data.length : 0);
      } catch (error) {
        setDemenagementNewCount(0);
      }
    };

    fetchPendingCounts();
    const interval = setInterval(fetchPendingCounts, 30000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Employee links
  // Employee navigation links - if profile incomplete, only show profile link
  const employeeLinks = user?.role === INTERNAL_ROLE ? [
    { path: '/internal/planning', label: 'Planning', icon: Calendar },
    { path: '/internal/hours', label: 'Mes heures', icon: Clock },
  ] : (isEmployee && user?.user_id && !isEmployeeProfileComplete) 
    ? [
        { path: '/profile', label: 'Mon profil', icon: Settings },
      ]
    : [
        { path: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
        { path: '/mes-statistiques', label: 'Mes Statistiques', icon: BarChart3 },
        { path: '/photos', label: 'Validation de Service', icon: CheckCircle },
        { path: '/history', label: 'Historique', icon: History },
        { path: '/planning', label: 'Mon planning', icon: Calendar },
        { path: '/demandes', label: 'Demandes', icon: Calendar },
        { path: '/mes-reclamations', label: 'Mes Réclamations', icon: AlertTriangle },
        { path: '/notifications', label: 'Notifications', icon: Bell },
        { path: '/profile', label: 'Mon profil', icon: Settings },
      ];

  // B2B Admin Links - Organized by sections
  const b2bAdminSections = [
    {
      title: 'Tableau de bord',
      icon: LayoutDashboard,
      links: [
        { path: '/admin/b2b', label: 'Tableau de bord', icon: LayoutDashboard },
        { path: '/admin/b2b/comments', label: 'Commentaires', icon: MessageSquare },
        { path: '/admin/b2b/emails', label: 'Mails', icon: Mail },
      ]
    },
    {
      title: 'Gestion du personnel',
      icon: Users,
      links: [
        { path: '/admin/b2b/employees', label: 'Employés', icon: Users },
        { path: '/admin/b2b/employees/deactivated-history', label: 'Historique des salariés désactivés', icon: Clock },
        ...(canManageInternal(user) ? [{ path: '/admin/b2b/internal-hours', label: 'Horaires internes', icon: Clock }] : []),
        { path: '/admin/b2b/candidatures', label: 'Candidatures', icon: Briefcase, badgeCount: pendingCandidaturesCount },
        { path: '/admin/b2b/trial-periods', label: 'Période d\'essai', icon: Clock, badgeCount: trialAlertsCount },
        ...(isFullAdmin || isSuperAdmin ? [{ path: '/admin/b2b/suivi-integration-rh', label: 'Suivi Intégration RH', icon: ClipboardList }] : []),
        { path: '/admin/b2b/formations-binome', label: 'Formation Binôme', icon: BookOpen, badgeCount: pendingFormationsCount },
        { path: '/admin/b2b/formations-online', label: 'Formation en ligne', icon: Video },
        { path: '/admin/b2b/rdv', label: 'RDV / Entretiens', icon: Briefcase },
      ]
    },
    {
      title: 'Gestion des missions',
      icon: Truck,
      links: [
        { path: '/admin/b2b/planning', label: 'Planning', icon: Calendar },
        { path: '/admin/b2b/validations', label: 'Validations missions', icon: CheckCircle, badgeCount: pendingValidationsCount },
        { path: '/admin/b2b/vehicles', label: 'Suivi VAN', icon: Truck },
        { path: '/admin/b2b/suivi-pda', label: 'Suivi PDA', icon: Smartphone },
        { path: '/admin/b2b/carburant', label: 'Suivi Carburant', icon: Fuel },
        { path: '/admin/b2b/kilometrique', label: 'Suivi Kilométrique', icon: Gauge },
      ]
    },
    {
      title: 'Gestion client',
      icon: Package,
      links: [
        { path: '/admin/b2b/quotes', label: 'Devis clients', icon: FileText },
        { path: '/admin/b2b/reclamations', label: 'Réclamations', icon: AlertTriangle },
        { path: '/admin/b2b/statistiques-livreurs', label: 'Stats Livreurs', icon: BarChart3 },
        { path: '/admin/b2b/presta/amazon', label: 'RJ AMZ', icon: BarChart3 },
        { path: '/admin/b2b/presta/amazon-suivi', label: 'Suivi Prestations AMZ', icon: ClipboardList },
        { path: '/admin/b2b/presta/colis-prive', label: 'RJ CP', icon: BarChart3 },
      ]
    },
    {
      title: 'Gestion financière',
      icon: Calculator,
      links: [
        { path: '/admin/b2b/calcul-salaire', label: 'Calcul Salaire', icon: Calculator },
      ]
    },
    {
      title: 'Suivi & alertes',
      icon: AlertTriangle,
      links: [
        { path: '/admin/b2b/demandes', label: 'Demandes repos', icon: Coffee, badgeCount: pendingDemandesCount },
        { path: '/admin/b2b/warnings', label: 'Rappels / Avert.', icon: AlertTriangle },
      ]
    },
  ];
  
  // Section Administration - uniquement pour le super admin (transport@dlv-express.com)
  const adminSection = {
    title: 'Administration',
    icon: Settings,
    links: [
      { path: '/admin/b2b/gestion-admins', label: 'Gestion des admins', icon: Shield },
    ]
  };
  
  // Ajouter la section Administration seulement pour le super admin
  const b2bAdminSectionsWithAdmin = isSuperAdmin 
    ? [...b2bAdminSections, adminSection] 
    : b2bAdminSections;

  // B2B Admin Links (flat list for compatibility)
  const b2bAdminLinks = b2bAdminSectionsWithAdmin.flatMap(section => section.links);

  // B2B Limited Admin Links
  const b2bLimitedAdminLinks = [
    { path: '/admin/b2b', label: 'Tableau de bord', icon: LayoutDashboard },
    { path: '/admin/b2b/planning', label: 'Planning', icon: Calendar },
    { path: '/admin/b2b/demandes', label: 'Demandes repos', icon: Coffee, badgeCount: pendingDemandesCount },
    { path: '/admin/b2b/formations-binome', label: 'Formation Binôme', icon: BookOpen, badgeCount: pendingFormationsCount },
    { path: '/admin/b2b/rdv', label: 'RDV / Entretiens', icon: Briefcase },
    { path: '/admin/b2b/presta/amazon', label: 'RJ AMZ', icon: BarChart3 },
    { path: '/admin/b2b/presta/amazon-suivi', label: 'Suivi Prestations AMZ', icon: ClipboardList },
    { path: '/admin/b2b/presta/colis-prive', label: 'RJ CP', icon: BarChart3 },
    { path: '/admin/b2b/vehicles', label: 'Suivi VAN', icon: Truck },
  ];

  // Admin limité Colis Privé - Accès uniquement au périmètre CP
  const b2bColisPriveAdminLinks = [
    { path: '/admin/b2b', label: 'Tableau de bord', icon: LayoutDashboard },
    { path: '/admin/b2b/planning', label: 'Planning CP', icon: Calendar },
    { path: '/admin/b2b/demandes', label: 'Demandes repos', icon: Coffee, badgeCount: pendingDemandesCount },
    { path: '/admin/b2b/presta/colis-prive', label: 'RJ Colis Privé', icon: BarChart3 },
    { path: '/admin/b2b/reclamations', label: 'Réclamations CP', icon: AlertTriangle },
    { path: '/admin/b2b/statistiques-livreurs', label: 'Stats Livreurs CP', icon: BarChart3 },
    { path: '/admin/b2b/employees/deactivated-history', label: 'Historique des salariés désactivés', icon: Clock },
    { path: '/admin/b2b/validations', label: 'Validations CP', icon: CheckCircle, badgeCount: pendingValidationsCount },
    { path: '/admin/b2b/suivi-pda', label: 'Suivi PDA', icon: Smartphone },
    { path: '/admin/b2b/carburant', label: 'Suivi Carburant', icon: Fuel },
    { path: '/admin/b2b/kilometrique', label: 'Suivi Kilométrique', icon: Gauge },
  ];

  // B2C Admin Links
  // B2C Admin Links - Structure spécifique pour la livraison particuliers
  const b2cAdminLinks = [
    { path: '/admin/b2c', label: 'Tableau de bord', icon: LayoutDashboard },
    { path: '/admin/b2c/demandes', label: 'Demandes clients', icon: FileText, badgeCount: b2cNewCount },
    { path: '/admin/b2c/devis', label: 'Devis', icon: Euro },
    { path: '/admin/b2c/missions', label: 'Missions / Interventions', icon: Truck },
    { path: '/admin/b2c/planning', label: 'Planning', icon: Calendar },
    { path: '/admin/b2c/clients', label: 'Clients', icon: Users },
    { path: '/admin/b2c/facturation', label: 'Facturation', icon: CreditCard },
  ];

  // Demenagement Admin Links - Structure spécifique pour le déménagement
  const demenagementAdminLinks = [
    { path: '/admin/demenagement', label: 'Tableau de bord', icon: LayoutDashboard },
    { path: '/admin/demenagement/demandes', label: 'Demandes de déménagement', icon: FileText, badgeCount: demenagementNewCount },
    { path: '/admin/demenagement/devis', label: 'Devis', icon: Euro },
    { path: '/admin/demenagement/missions', label: 'Missions de déménagement', icon: Truck },
    { path: '/admin/demenagement/planning', label: 'Planning équipes / camions', icon: Calendar },
    { path: '/admin/demenagement/inventaires', label: 'Inventaires / Volumes', icon: Package },
    { path: '/admin/demenagement/clients', label: 'Clients', icon: Users },
  ];

  // Get links based on current module
  const getAdminLinks = () => {
    if (!isAdmin) return employeeLinks;
    
    switch (currentModule) {
      case 'b2c':
        return b2cAdminLinks;
      case 'demenagement':
        return demenagementAdminLinks;
      case 'b2b':
      default:
        if (isColisPriveAdmin) return b2bColisPriveAdminLinks;
        if (isLimitedAdmin) return b2bLimitedAdminLinks;
        return b2bAdminLinks;
    }
  };

  const links = getAdminLinks();

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    sessionStorage.clear();
    document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    navigate('/login', { replace: true });
  };

  // Module color classes
  const moduleColors = {
    b2b: 'bg-slate-800',
    b2c: 'bg-blue-600',
    demenagement: 'bg-orange-500'
  };

  const sidebarBgColor = currentModule ? moduleColors[currentModule] : 'bg-[#0F172A]';

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar - Desktop */}
      <aside className={`hidden lg:flex lg:flex-col lg:w-64 ${sidebarBgColor} text-white`}>
        {/* Logo + Back to Home */}
        <div className="p-4 border-b border-white/10">
          <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center justify-center">
            <img src={LOGO_URL} alt="DLV EXPRESS" className="h-14 w-auto" />
          </Link>
        </div>

        {/* Module indicator + Back button for admin (not for Colis Privé admin) */}
        {isAdmin && currentModule && !isColisPriveAdmin && (
          <div className="px-4 py-3 border-b border-white/10">
            <Link 
              to="/admin"
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Changer d&apos;activité</span>
            </Link>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${currentModule === 'b2b' ? 'bg-white' : currentModule === 'b2c' ? 'bg-blue-300' : 'bg-orange-300'}`}></div>
              <span className="font-medium text-sm">{moduleConfig?.name}</span>
            </div>
          </div>
        )}
        
        {/* For Colis Privé admin - show fixed module indicator without back button */}
        {isAdmin && isColisPriveAdmin && (
          <div className="px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-white"></div>
              <span className="font-medium text-sm">Colis Privé</span>
            </div>
          </div>
        )}

        {/* User info */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-white font-medium">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name}</p>
              <p className="text-gray-300 text-sm truncate">
                {isAdmin ? (isColisPriveAdmin ? 'Admin Colis Privé' : isLimitedAdmin ? 'Admin (limité)' : 'Administrateur') : 'Employé'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          {/* For B2B module, show collapsible sectioned navigation */}
          {currentModule === 'b2b' && isAdmin && !isLimitedAdmin && !isColisPriveAdmin ? (
            <div className="space-y-1">
              {b2bAdminSectionsWithAdmin.map((section) => {
                const isExpanded = expandedSections[section.title];
                const sectionBadgeCount = section.links.reduce((sum, link) => sum + (link.badgeCount || 0), 0);
                const hasActiveLink = section.links.some(link => location.pathname === link.path);
                
                return (
                  <div key={section.title}>
                    {/* Section header - clickable to expand/collapse */}
                    <button
                      onClick={() => toggleSection(section.title)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                        hasActiveLink 
                          ? 'bg-white/15 text-white' 
                          : 'text-white/70 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <section.icon className="w-4 h-4" />
                      <span className="text-sm font-medium flex-1 text-left">{section.title}</span>
                      {sectionBadgeCount > 0 && (
                        <span className="min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {sectionBadgeCount > 99 ? '99+' : sectionBadgeCount}
                        </span>
                      )}
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-white/50" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-white/50" />
                      )}
                    </button>
                    
                    {/* Section links - collapsible */}
                    {isExpanded && (
                      <div className="ml-4 mt-1 space-y-0.5 border-l border-white/10 pl-2">
                        {section.links.map((link) => (
                          <Link
                            key={link.path}
                            to={link.path}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                              location.pathname === link.path 
                                ? 'bg-white/20 text-white' 
                                : 'text-white/60 hover:text-white hover:bg-white/10'
                            }`}
                            data-testid={`nav-${link.path.replace(/\//g, '-')}`}
                          >
                            <link.icon className="w-4 h-4" />
                            <span className="text-sm">{link.label}</span>
                            {link.badgeCount > 0 && (
                              <span className="ml-auto min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                                {link.badgeCount > 99 ? '99+' : link.badgeCount}
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Logout button - closer to menu */}
              <div className="mt-4 pt-3 border-t border-white/10">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all duration-200"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-sm">Déconnexion</span>
                </button>
              </div>
            </div>
          ) : (
            /* For other modules or limited admin, show flat navigation */
            <div className="space-y-1">
              {links.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                    location.pathname === link.path 
                      ? 'bg-white/20 text-white' 
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid={`nav-${link.path.replace(/\//g, '-')}`}
                >
                  <link.icon className="w-5 h-5" />
                  <span className="text-sm">{link.label}</span>
                  {link.badgeCount > 0 && (
                    <span className="ml-auto min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                      {link.badgeCount > 99 ? '99+' : link.badgeCount}
                    </span>
                  )}
                </Link>
              ))}
              
              {/* Logout button for non-B2B modules */}
              <div className="mt-4 pt-3 border-t border-white/10">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all duration-200"
                  data-testid="logout-btn-flat"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-sm">Déconnexion</span>
                </button>
              </div>
            </div>
          )}
        </nav>
      </aside>

      {/* Mobile Header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 ${sidebarBgColor} z-40 px-4 py-3`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isAdmin && currentModule && (
              <Link to="/admin" className="text-white/70 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            )}
            <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center gap-2">
              <img src={LOGO_URL} alt="DLV EXPRESS" className="h-10 w-auto" />
            </Link>
            {moduleConfig && (
              <span className="text-white/70 text-sm hidden sm:block">| {moduleConfig.name}</span>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white p-2"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed top-0 left-0 bottom-0 w-64 ${sidebarBgColor} z-50 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ display: 'flex', flexDirection: 'column', height: '100vh', maxHeight: '100vh' }}
      >
        <div className="p-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center justify-between">
            <img src={LOGO_URL} alt="DLV EXPRESS" className="h-10 w-auto" />
            <button onClick={() => setSidebarOpen(false)} className="text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Module indicator + Back button (hidden for Colis Privé admin) */}
        {isAdmin && currentModule && !isColisPriveAdmin && (
          <div className="px-4 py-3 border-b border-white/10 flex-shrink-0">
            <Link 
              to="/admin"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Changer d&apos;activité</span>
            </Link>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${currentModule === 'b2b' ? 'bg-white' : currentModule === 'b2c' ? 'bg-blue-300' : 'bg-orange-300'}`}></div>
              <span className="font-medium text-sm text-white">{moduleConfig?.name}</span>
            </div>
          </div>
        )}
        {/* For Colis Privé admin mobile - show fixed module indicator without back button */}
        {isAdmin && isColisPriveAdmin && (
          <div className="px-4 py-3 border-b border-white/10 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-white"></div>
              <span className="font-medium text-sm text-white">Colis Privé</span>
            </div>
          </div>
        )}

        <div className="p-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-white font-medium">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <p className="text-white font-medium">{user?.name}</p>
              <p className="text-gray-300 text-sm">
                {isAdmin ? (isColisPriveAdmin ? 'Admin CP' : isLimitedAdmin ? 'Admin (limité)' : 'Admin') : 'Employé'}
              </p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto min-h-0">
          {/* For B2B module, show collapsible sectioned navigation */}
          {currentModule === 'b2b' && isAdmin && !isLimitedAdmin && !isColisPriveAdmin ? (
            <div className="space-y-1">
              {b2bAdminSectionsWithAdmin.map((section) => {
                const isExpanded = expandedSections[section.title];
                const sectionBadgeCount = section.links.reduce((sum, link) => sum + (link.badgeCount || 0), 0);
                const hasActiveLink = section.links.some(link => location.pathname === link.path);
                
                return (
                  <div key={section.title}>
                    {/* Section header - clickable */}
                    <button
                      onClick={() => toggleSection(section.title)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                        hasActiveLink 
                          ? 'bg-white/15 text-white' 
                          : 'text-white/70 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <section.icon className="w-4 h-4" />
                      <span className="text-sm font-medium flex-1 text-left">{section.title}</span>
                      {sectionBadgeCount > 0 && (
                        <span className="min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {sectionBadgeCount > 99 ? '99+' : sectionBadgeCount}
                        </span>
                      )}
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-white/50" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-white/50" />
                      )}
                    </button>
                    
                    {/* Section links - collapsible */}
                    {isExpanded && (
                      <div className="ml-4 mt-1 space-y-0.5 border-l border-white/10 pl-2">
                        {section.links.map((link) => (
                          <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                              location.pathname === link.path 
                                ? 'bg-white/20 text-white' 
                                : 'text-white/60 hover:text-white hover:bg-white/10'
                            }`}
                          >
                            <link.icon className="w-4 h-4" />
                            <span className="text-sm">{link.label}</span>
                            {link.badgeCount > 0 && (
                              <span className="ml-auto min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                {link.badgeCount > 99 ? '99+' : link.badgeCount}
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Logout button - mobile B2B */}
              <div className="mt-4 pt-3 border-t border-white/10">
                <button
                  onClick={() => { setSidebarOpen(false); handleLogout(); }}
                  className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-sm">Déconnexion</span>
                </button>
              </div>
            </div>
          ) : (
            /* For other modules, show flat navigation */
            <div className="space-y-1">
              {links.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                    location.pathname === link.path 
                      ? 'bg-white/20 text-white' 
                      : 'text-white/70 hover:text-white hover:bg-white/10 active:bg-white/20'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{link.label}</span>
                  {link.badgeCount > 0 && (
                    <span className="ml-auto min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {link.badgeCount > 99 ? '99+' : link.badgeCount}
                    </span>
                  )}
                </Link>
              ))}
              
              {/* Logout button - mobile other modules */}
              <div className="mt-4 pt-3 border-t border-white/10">
                <button
                  onClick={() => { setSidebarOpen(false); handleLogout(); }}
                  className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid="mobile-logout-btn-flat"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-sm">Déconnexion</span>
                </button>
              </div>
            </div>
          )}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-0 pt-16 lg:pt-0 pb-32 lg:pb-8 min-h-screen overflow-y-auto">
        <div className="p-4 lg:p-8 pb-24 lg:pb-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30 safe-area-bottom">
        <div className="flex justify-around py-1 px-1">
          {links.slice(0, 4).map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`flex flex-col items-center justify-center min-h-[56px] min-w-[56px] flex-1 rounded-xl transition-colors ${
                  isActive 
                    ? currentModule === 'b2c' ? 'text-blue-600 bg-blue-50' : currentModule === 'demenagement' ? 'text-orange-600 bg-orange-50' : 'text-slate-800 bg-slate-100'
                    : 'text-gray-400 active:bg-gray-100'
                }`}
                data-testid={`bottom-nav-${link.path.replace(/\//g, '-')}`}
              >
                <link.icon className={`${isActive ? 'w-6 h-6' : 'w-5 h-5'}`} />
                <span className={`text-[10px] mt-0.5 font-medium truncate max-w-[64px] ${isActive ? 'text-xs' : ''}`}>
                  {link.label.length > 10 ? link.label.split(' ')[0] : link.label}
                </span>
                {link.badgeCount > 0 && (
                  <span className="absolute -top-1 right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {link.badgeCount > 9 ? '9+' : link.badgeCount}
                  </span>
                )}
              </Link>
            );
          })}
          <button
            onClick={() => setSidebarOpen(true)}
            className="flex flex-col items-center justify-center min-h-[56px] min-w-[56px] flex-1 rounded-xl text-gray-400 active:bg-gray-100 transition-colors"
            data-testid="mobile-bottom-menu-btn"
          >
            <Menu className="w-5 h-5" />
            <span className="text-[10px] mt-0.5 font-medium">Menu</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Layout;
